import { z } from 'zod'

import { idSchema } from '../common.ts'
import { CitedPostSchema, RecommendationDimensionEnum } from '../recommendation.ts'

export const AgentEvidenceUseEnum = z.enum(['slot_choice', 'caption_phrasing', 'hashtags', 'creator_guidance'])
export type AgentEvidenceUse = z.infer<typeof AgentEvidenceUseEnum>

export const AgentEvidenceStatusEnum = z.enum(['grounded', 'insufficient_data'])
export type AgentEvidenceStatus = z.infer<typeof AgentEvidenceStatusEnum>

export const MAX_EVIDENCE_REFERENCES = 8
export const MAX_CITED_POSTS_PER_RECOMMENDATION = 20
export const AGENT_EXPLORATION_WINDOW_MS = 90 * 60 * 1000

export const AgentEvidenceReferenceSchema = z.object({
  recommendationId: idSchema,
  usedFor: AgentEvidenceUseEnum
})
export type AgentEvidenceReference = z.infer<typeof AgentEvidenceReferenceSchema>

const hasUniqueEvidenceSelections = (selections: readonly { recommendationId: string; usedFor: string }[]): boolean => {
  const keys = selections.map((selection) => `${selection.recommendationId}:${selection.usedFor}`)
  return new Set(keys).size === keys.length
}

export const AgentEvidenceReferencesSchema = z
  .array(AgentEvidenceReferenceSchema)
  .max(MAX_EVIDENCE_REFERENCES)
  .refine(hasUniqueEvidenceSelections, { message: 'evidence references must be unique by recommendation and use' })
export type AgentEvidenceReferences = z.infer<typeof AgentEvidenceReferencesSchema>

// RAL-239: one sentence on why a lane differs from the base proposal, grounded in one recommendation
// from that lane's own channel. Lives here rather than in proposal.ts so delivery.ts can reuse it
// without importing proposal.ts, which already imports delivery.ts.
export const MAX_LANE_RATIONALE_CHARS = 140
export const LaneRationaleSchema = z.string().trim().min(1).max(MAX_LANE_RATIONALE_CHARS)
export const LaneRationaleCitationSchema = z.object({ recommendationId: idSchema, claim: z.string().min(1) })
export type LaneRationaleCitation = z.infer<typeof LaneRationaleCitationSchema>

export const ProposalEvidenceCitationSchema = AgentEvidenceReferenceSchema.extend({
  claim: z.string().min(1),
  citedPosts: z.array(CitedPostSchema).min(1).max(MAX_CITED_POSTS_PER_RECOMMENDATION)
})
export type ProposalEvidenceCitation = z.infer<typeof ProposalEvidenceCitationSchema>

export const PromptEvidenceSchema = z.object({
  id: idSchema,
  accountId: idSchema,
  dimension: RecommendationDimensionEnum,
  key: z.string().min(1),
  claim: z.string().min(1),
  sampleSize: z.number().int().nonnegative(),
  aboveMedianCount: z.number().int().nonnegative(),
  citedPosts: z.array(CitedPostSchema).min(1).max(MAX_CITED_POSTS_PER_RECOMMENDATION)
})
export type PromptEvidence = z.infer<typeof PromptEvidenceSchema>

export const AgentExplorationDirectiveSchema = z
  .object({
    assetId: idSchema,
    timeBinStartHour: z.number().int().min(0).max(21),
    windowStartAt: z.number().int().nonnegative(),
    windowEndAt: z.number().int().nonnegative(),
    timezone: z.string().min(1)
  })
  .refine(({ windowStartAt, windowEndAt }) => windowEndAt > windowStartAt, {
    message: 'exploration window must end after it starts'
  })
  .refine(({ windowStartAt, windowEndAt }) => windowEndAt - windowStartAt === AGENT_EXPLORATION_WINDOW_MS, {
    message: 'exploration window must be 90 minutes'
  })
export type AgentExplorationDirective = z.infer<typeof AgentExplorationDirectiveSchema>

const ProposalEvidenceCitationsSchema = z
  .array(ProposalEvidenceCitationSchema)
  .max(MAX_EVIDENCE_REFERENCES)
  .refine(hasUniqueEvidenceSelections, { message: 'evidence citations must be unique by recommendation and use' })

export const ProposalEvidenceSchema = z
  .object({
    status: AgentEvidenceStatusEnum,
    citations: ProposalEvidenceCitationsSchema,
    exploration: AgentExplorationDirectiveSchema.nullable()
  })
  .superRefine(({ status, citations }, ctx) => {
    const grounded = status === AgentEvidenceStatusEnum.enum.grounded
    const coherent = grounded ? citations.length > 0 : citations.length === 0
    if (!coherent) {
      ctx.addIssue({ code: 'custom', message: 'evidence status and citations must agree' })
    }
  })
export type ProposalEvidence = z.infer<typeof ProposalEvidenceSchema>
